import { useState, useEffect, useCallback } from "react";
import {
  activeOrchestrations,
  FermentationOrchestrator,
  intentKey,
} from "@brewery/services/fermentationOrchestrator";

function resolvePendingDevice(breweryList, current) {
  if (!breweryList) return null;

  const devices = [
    ...(breweryList.brew_clean_idle || []),
    ...(breweryList.brew_acid_clean_idle || []),
    ...(breweryList.fermenting || []),
    ...(breweryList.serving || []),
  ];

  // activeOrchestrations: an intent saved by an orchestration running in
  // this tab (modal-driven or banner-resumed) must not surface a banner —
  // the intent stays in localStorage until DONE, and every MQTT ack
  // triggers a refetch that lands here.
  const pending = devices.find((d) => {
    if (d.device_type !== 1 || activeOrchestrations.has(d.uuid)) {
      return false;
    }
    try {
      return Boolean(localStorage.getItem(intentKey(d.uuid)));
    } catch (e) {
      return false;
    }
  });

  if (pending && !current) {
    return pending;
  }
  if (current) {
    let hasIntent = false;
    try {
      hasIntent = Boolean(localStorage.getItem(intentKey(current.uuid)));
    } catch (e) {
      hasIntent = false;
    }
    if (!hasIntent) {
      // The intent completed/expired elsewhere — retire the stale banner.
      return null;
    }
    return current;
  }
  return null;
}

export function usePendingFermentation(breweryList) {
  const [prevBreweryList, setPrevBreweryList] = useState(breweryList);
  const [pendingFermentationDevice, setPendingFermentationDevice] = useState(
    () => resolvePendingDevice(breweryList, null),
  );

  if (breweryList !== prevBreweryList) {
    setPrevBreweryList(breweryList);
    const nextDevice = resolvePendingDevice(
      breweryList,
      pendingFermentationDevice,
    );
    if (nextDevice !== pendingFermentationDevice) {
      setPendingFermentationDevice(nextDevice);
    }
  }

  useEffect(() => {
    if (!breweryList) return;
    const nextDevice = resolvePendingDevice(
      breweryList,
      pendingFermentationDevice,
    );
    if (nextDevice !== pendingFermentationDevice) {
      setPendingFermentationDevice(nextDevice);
    }
  }, [breweryList, pendingFermentationDevice]);

  const resumePendingFermentation = useCallback(async () => {
    if (!pendingFermentationDevice) return;
    try {
      const orch = FermentationOrchestrator.resumeFromIntent(
        pendingFermentationDevice.uuid,
        {},
      );
      setPendingFermentationDevice(null);
      // run() never rejects (errors land in its ERROR phase); the orchestrator
      // registers itself in activeOrchestrations while running — ERROR keeps
      // the intent, so the banner returns as the recovery entry point.
      if (orch && typeof orch.run === "function") {
        await orch.run();
      }
    } catch (err) {
      // ignore
    }
  }, [pendingFermentationDevice]);

  const dismissPendingFermentation = useCallback(() => {
    if (!pendingFermentationDevice) return;
    try {
      localStorage.removeItem(intentKey(pendingFermentationDevice.uuid));
    } catch (err) {
      // ignore
    }
    setPendingFermentationDevice(null);
  }, [pendingFermentationDevice]);

  return {
    pendingFermentationDevice,
    resumePendingFermentation,
    dismissPendingFermentation,
    setPendingFermentationDevice,
  };
}

export default usePendingFermentation;
